/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed, action } from '@ember/object';
import Component from '@glimmer/component';
import { isNone } from '@ember/utils';
import { CampaignType } from 'cio/models/campaign';
import CampaignModel from 'cio/models/campaign';
import SubjectStepModel from 'cio/models/subject-step';

interface ContentArgs {
  step: SubjectStepModel;
  campaign?: CampaignModel;
  onMouseEnter: () => unknown;
  onMouseLeave: () => unknown;
}

// First attribute of the step to be truthy defines description
const DESCRIPTION_ATTRS: (keyof SubjectStepModel)[] = [
  'isSkeleton',
  'draft',
  'isSkipped',
  'isHold',
  'isDelay',
  'isDelivery',
  'isSplitTest',
  'isMultiLangBranch',
  'isMatch',
  'isExit',
  'isGracePeriod',
];

const DESCRIPTIONS: Record<keyof SubjectStepModel & 'default', string> = {
  isSkeleton: '…',
  draft: 'Draft',
  isSkipped: 'Skip',
  isHold: 'Wait in',
  isDelay: 'Wait in',
  isDelivery: 'Send',
  isSplitTest: 'Perform A/B',
  isMatch: 'Met campaign conditions, then…',
  isExit: 'Leave campaign.',
  isGracePeriod: 'Wait in',
  default: '(Unknown step)',
};

const CAMPAIGN_TYPE_DESCRIPTION_OVERRIDES: Partial<
  Record<CampaignType, Record<keyof SubjectStepModel & 'default', string>>
> = {
  [CampaignType.APITriggered]: {
    ...DESCRIPTIONS,
    isMatch: 'The broadcast was triggered, then…',
    isExit: '…completed!',
  },
  [CampaignType.Webhook]: {
    ...DESCRIPTIONS,
    isMatch: 'Workflow triggered by webhook…',
    isExit: 'Finished workflow',
  },
};

const PAST_DESCRIPTIONS = {
  Draft: 'Drafted',
  Skip: 'Skipped',
  'Wait in': 'Waited in',
  Send: 'Sent',
  'Leave campaign.': 'Left campaign.',
};

const CURRENT_DESCRIPTIONS = {
  'Wait in': 'Waiting in',
};

export default class Content extends Component<ContentArgs> {
  get showHelp() {
    return this.args.step.isGracePeriod;
  }

  @computed(
    `args.step.{${DESCRIPTION_ATTRS.join(',')}}`,
    'args.step.{current,future,isNamed}',
    'args.campaign.isApiTriggered',
  )
  get description() {
    let { step } = this.args;
    let attr = (DESCRIPTION_ATTRS.find((a) => step[a]) ?? 'default') as keyof SubjectStepModel & 'default';

    let campaignType = this.args.campaign?.type;
    let descriptions: Record<keyof SubjectStepModel & 'default', string> = DESCRIPTIONS;

    if (campaignType != null) {
      let typeDescriptions = CAMPAIGN_TYPE_DESCRIPTION_OVERRIDES[campaignType];

      if (typeDescriptions != null) {
        descriptions = typeDescriptions;
      }
    }
    let description = descriptions[attr];

    if (step.get('current')) {
      description = CURRENT_DESCRIPTIONS[description] || description;
    } else if (!step.get('future')) {
      description = PAST_DESCRIPTIONS[description] || description;
    }

    let shortName = '';
    if (step.showActionTypeName) {
      shortName = step.splitAction?.action.info.shortName ?? step.action?.info.shortName ?? '';
    }

    return `${description} ${shortName}`.trim();
  }

  @computed('args.step.{isDelay,isGracePeriod,entered,exited,due}')
  get showDuration() {
    let { isDelay, isHold, isGracePeriod, entered, exited, due } = this.args.step;
    let end = exited || due;

    return (isDelay || isHold || isGracePeriod) && !isNone(end) && entered !== end;
  }

  @computed('args.step.{isSplitTest,hasDelivery,isNamed}')
  get showName() {
    let { step } = this.args;

    if (step.get('isSplitTest')) {
      return step.get('hasDelivery');
    }

    return step.get('isNamed');
  }

  @action
  onMouseEnter() {
    if (typeof this.args.onMouseEnter === 'function') {
      this.args.onMouseEnter();
    }
  }

  @action
  onMouseLeave() {
    if (typeof this.args.onMouseLeave === 'function') {
      this.args.onMouseLeave();
    }
  }
}
